import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const eur  = n => '€ ' + parseFloat(n||0).toLocaleString('it-IT', { minimumFractionDigits:2 });
const fmtD = d => !d || d==='0000-00-00' ? '—' : d.substring(0,10).split('-').reverse().join('/');
const oggi = new Date(); oggi.setHours(0,0,0,0);
const diffGiorni = d => { const t = new Date(d); t.setHours(0,0,0,0); return Math.round((t-oggi)/86400000); };

function StatCard({ icon, label, value, color, onClick }) {
  return (
    <div onClick={onClick} style={{
      background:'#fff', borderRadius:10, padding:'14px 16px',
      border:'1px solid #e5e7eb', cursor:onClick?'pointer':'default',
      borderTop:`3px solid ${color||'#b30000'}`,
      display:'flex', alignItems:'center', gap:12,
    }}>
      <div style={{ fontSize:26 }}>{icon}</div>
      <div>
        <div style={{ fontSize:22, fontWeight:700, color:color||'#1a1a1a' }}>{value}</div>
        <div style={{ fontSize:12, color:'#6b7280' }}>{label}</div>
      </div>
    </div>
  );
}

function SectionCard({ title, icon, children, action, actionLabel }) {
  return (
    <div className="card" style={{ padding:0 }}>
      <div style={{ padding:'10px 14px', borderBottom:'1px solid #e5e7eb', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontWeight:600, fontSize:13 }}>{icon} {title}</span>
        {action && <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }} onClick={action}>{actionLabel}</button>}
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ msg }) {
  return <div style={{ padding:'12px 14px', fontSize:12, color:'#9ca3af', textAlign:'center' }}>{msg}</div>;
}

function Row({ onClick, children }) {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{ padding:'8px 14px', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:10, cursor:'pointer', background:hover?'#fafafa':'#fff' }}>
      {children}
    </div>
  );
}

// ── FATTURE SCADUTE ───────────────────────────────────────────
function FattureScadute() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/fic/fatture')
      .then(r => {
        const statoFattura = f => {
          const rate = (f.payments_list || []).filter(p => p.status !== 'reversed');
          if (!rate.length) return f.payment_status || 'not_paid';
          if (rate.every(p => p.status === 'paid')) return 'paid';
          if (rate.some(p => p.status === 'paid'))  return 'partial';
          const oggi = new Date(); oggi.setHours(0,0,0,0);
          return rate.some(p => p.status === 'overdue' ||
                                (p.due_date && new Date(p.due_date) < oggi))
                 ? 'overdue' : 'not_paid';
        };
        const all = (r.data?.data||[])
          .map(f => ({ ...f, status: statoFattura(f) }))
          .filter(f => f.status === 'overdue' || f.status === 'not_paid')
          .sort((a,b) => (a.status === b.status ? 0 : a.status === 'overdue' ? -1 : 1));
        all.sort((a,b)=>new Date(a.payment_terms||0)-new Date(b.payment_terms||0));
        setItems(all.slice(0,6));
      }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div style={{padding:12,textAlign:'center'}}><div className="spinner"></div></div>;
  if (!items.length) return <EmptyRow msg="✅ Nessuna fattura scaduta" />;

  return items.map(f => {
    const g = f.payment_terms ? diffGiorni(f.payment_terms) : null;
    return (
      <Row key={f.id} onClick={()=>nav('/fatture')}>
        <span>📄</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:500,fontSize:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.number} — {f.entity?.name||'—'}</div>
          <div style={{fontSize:11,color:'#9ca3af'}}>Scad. {fmtD(f.payment_terms)} {g!==null&&g<0&&<span style={{color:'#dc2626'}}>({Math.abs(g)}gg fa)</span>}</div>
        </div>
        <div style={{fontWeight:700,fontSize:13,color:'#b30000',whiteSpace:'nowrap'}}>{eur(f.amount_gross)}</div>
        <span className={`badge ${f.status==='overdue'?'badge-red':'badge-orange'}`} style={{fontSize:10}}>{f.status==='overdue'?'Scaduta':'Non pagata'}</span>
      </Row>
    );
  });
}

// ── CONTRATTI IN SCADENZA ─────────────────────────────────────
function ContrattiInScadenza() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/contratti').then(r => {
      const all = (Array.isArray(r.data) ? r.data : (r.data?.data||[])).filter(c => c.data_fine && c.stato!=='scaduto' && diffGiorni(c.data_fine)<=90);
      all.sort((a,b)=>diffGiorni(a.data_fine)-diffGiorni(b.data_fine));
      setItems(all.slice(0,6));
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div style={{padding:12,textAlign:'center'}}><div className="spinner"></div></div>;
  if (!items.length) return <EmptyRow msg="✅ Nessun contratto in scadenza nei prossimi 90 giorni" />;

  return items.map(c => {
    const g = diffGiorni(c.data_fine);
    const col = g<0?'#dc2626':g<=30?'#d97706':'#2563eb';
    return (
      <Row key={c.id} onClick={()=>nav('/contratti')}>
        <span>📋</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:500,fontSize:12}}>{c.numero_protocollo} — {c.cliente_nome}</div>
          <div style={{fontSize:11,color:'#9ca3af'}}>Scade il {fmtD(c.data_fine)}</div>
        </div>
        <span style={{fontSize:12,fontWeight:600,color:col,whiteSpace:'nowrap'}}>{g<0?`${Math.abs(g)}gg fa`:`tra ${g}gg`}</span>
      </Row>
    );
  });
}

// ── PROSSIMI INTERVENTI ───────────────────────────────────────
function ProssimiInterventi() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/interventi').then(r => {
      const all = (Array.isArray(r.data) ? r.data : (r.data?.data||[]))
        .filter(i=>i.stato==='programmato'||i.stato==='in_corso')
        .sort((a,b)=>new Date(a.data_intervento)-new Date(b.data_intervento))
        .slice(0,6);
      setItems(all);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div style={{padding:12,textAlign:'center'}}><div className="spinner"></div></div>;
  if (!items.length) return <EmptyRow msg="Nessun intervento programmato" />;

  return items.map(i => {
    const g = diffGiorni(i.data_intervento);
    return (
      <Row key={i.id} onClick={()=>nav(`/interventi/${i.id}`)}>
        <span>{i.stato==='in_corso'?'🔧':'📅'}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:500,fontSize:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{i.sito_nome}</div>
          <div style={{fontSize:11,color:'#9ca3af'}}>{i.note||'—'}</div>
        </div>
        <div style={{textAlign:'right',whiteSpace:'nowrap'}}>
          <div style={{fontSize:12,fontWeight:600,color:g<0?'#dc2626':g===0?'#d97706':'#374151'}}>
            {g<0?`${Math.abs(g)}gg fa`:g===0?'Oggi':fmtD(i.data_intervento)}
          </div>
          <span className={`badge ${i.stato==='in_corso'?'badge-orange':'badge-blue'}`} style={{fontSize:10}}>
            {i.stato==='in_corso'?'In corso':'Programmato'}
          </span>
        </div>
      </Row>
    );
  });
}

// ── MATERIALI ESAURITI ────────────────────────────────────────
function MaterialiEsauriti() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/fic/prodotti?per_page=100&page=1').then(r => {
      const all = (r.data?.data||[])
        .filter(p=>p.stock_minimum>0&&p.stock_current!==null&&p.stock_current<=p.stock_minimum)
        .sort((a,b)=>(a.stock_current||0)-(b.stock_current||0))
        .slice(0,6);
      setItems(all);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div style={{padding:12,textAlign:'center'}}><div className="spinner"></div></div>;
  if (!items.length) return <EmptyRow msg="✅ Tutti i prodotti nella norma" />;

  return items.map(p => {
    const esaurito = p.stock_current<=0;
    return (
      <Row key={p.id} onClick={()=>nav('/magazzino')}>
        <span>🧯</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:500,fontSize:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
          <div style={{fontSize:11,color:'#9ca3af'}}>Soglia min: {p.stock_minimum}</div>
        </div>
        <span className={`badge ${esaurito?'badge-red':'badge-orange'}`} style={{fontSize:10}}>
          {esaurito?'🚨 Esaurito':`⚠️ ${p.stock_current} pz`}
        </span>
      </Row>
    );
  });
}

// ── MINI MAPPA FLOTTA ─────────────────────────────────────────
function MiniMappa() {
  const mapRef  = useRef(null);
  const leafRef = useRef(null);
  const [online, setOnline] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    const loadL = () => new Promise(res => {
      if (window.L) { res(window.L); return; }
      const link = document.createElement('link');
      link.rel='stylesheet';
      link.href='https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(link);
      const s = document.createElement('script');
      s.src='https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
      s.onload=()=>res(window.L);
      document.head.appendChild(s);
    });

    loadL().then(L => {
      if (leafRef.current||!mapRef.current) return;
      leafRef.current = L.map(mapRef.current, {zoomControl:false, attributionControl:false})
        .setView([41.9,12.5],5);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(leafRef.current);

      api.get('/flotta/posizioni').then(r => {
        const pos = (r.data||[]).filter(p=>p.latitude&&p.longitude);
        setOnline(pos.length);
        pos.forEach(p => {
          const icon = L.divIcon({
            className:'',
            html:`<div style="background:#b30000;color:white;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,.3)">🚐</div>`,
            iconSize:[26,26],iconAnchor:[13,13],
          });
          L.marker([p.latitude,p.longitude],{icon}).addTo(leafRef.current);
        });
        if (pos.length>0) {
          const bounds = L.latLngBounds(pos.map(p=>[p.latitude,p.longitude]));
          leafRef.current.fitBounds(bounds,{padding:[20,20],maxZoom:12});
        }
      }).catch(()=>{});
    });

    return ()=>{ if(leafRef.current){leafRef.current.remove();leafRef.current=null;} };
  }, []);

  return (
    <div style={{position:'relative'}}>
      <div ref={mapRef} style={{height:180}} />
      <div style={{position:'absolute',bottom:8,left:8,zIndex:1000,background:'rgba(255,255,255,0.9)',padding:'3px 10px',borderRadius:6,fontSize:12,fontWeight:600}}>
        🚐 {online} veicol{online===1?'o':'i'} online
      </div>
      <button onClick={()=>nav('/flotta')} style={{position:'absolute',top:8,right:8,zIndex:1000,background:'#b30000',color:'white',border:'none',borderRadius:6,padding:'4px 10px',fontSize:11,cursor:'pointer'}}>
        Apri flotta →
      </button>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────
export default function Dashboard() {
  const [stats,   setStats]   = useState({ programmati:0,in_corso:0,completati:0,clienti:0,contratti_attivi:0 });
  const [loading, setLoading] = useState(true);
  const { user }  = useAuth();
  const nav       = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/interventi/stats').catch(()=>({data:{data:{}}})),
      api.get('/clienti').catch(()=>({data:{data:[]}})),
      api.get('/contratti').catch(()=>({data:[]})),
    ]).then(([s,c,k]) => {
      const st = s.data?.data||{};
      // contratti API returns array directly
      const contratti = Array.isArray(k.data) ? k.data : (k.data?.data||[]);
      const clienti   = Array.isArray(c.data) ? c.data : (c.data?.data||[]);
      setStats({
        programmati:      st.programmati||0,
        in_corso:         st.in_corso||0,
        completati:       st.completati||0,
        clienti:          clienti.length,
        contratti_attivi: contratti.filter(x=>x.stato==='attivo').length,
      });
    }).catch(console.error).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  const giorno = new Date().toLocaleDateString('it-IT',{weekday:'long',day:'numeric',month:'long',year:'numeric'});

  return (
    <div className="page-content">
      <div style={{marginBottom:16}}>
        <h2 style={{margin:'0 0 2px',fontSize:18}}>
          Buongiorno{user?.nome?', '+user.nome.split(' ')[0]:''} 👋
        </h2>
        <p style={{margin:0,fontSize:12,color:'#9ca3af'}}>{giorno.charAt(0).toUpperCase()+giorno.slice(1)}</p>
      </div>

      {/* CONTATORI */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:10,marginBottom:16}}>
        <StatCard icon="👥" label="Clienti"          value={stats.clienti}          color="#2563eb" onClick={()=>nav('/clienti')} />
        <StatCard icon="📋" label="Contratti attivi" value={stats.contratti_attivi} color="#16a34a" onClick={()=>nav('/contratti')} />
        <StatCard icon="📅" label="Programmati"      value={stats.programmati}      color="#d97706" onClick={()=>nav('/interventi')} />
        <StatCard icon="🔧" label="In corso"         value={stats.in_corso}         color="#b30000" onClick={()=>nav('/interventi')} />
        <StatCard icon="✅" label="Completati"       value={stats.completati}       color="#6b7280" onClick={()=>nav('/interventi')} />
      </div>

      {/* GRIGLIA 2 COLONNE */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
        <SectionCard title="Fatture scadute / non pagate" icon="💰" action={()=>nav('/fatture')} actionLabel="Tutte →">
          <FattureScadute />
        </SectionCard>
        <SectionCard title="Contratti in scadenza (90gg)" icon="📋" action={()=>nav('/contratti')} actionLabel="Tutti →">
          <ContrattiInScadenza />
        </SectionCard>
        <SectionCard title="Prossimi interventi" icon="📅" action={()=>nav('/interventi')} actionLabel="Tutti →">
          <ProssimiInterventi />
        </SectionCard>
        <SectionCard title="Materiali esauriti / sotto soglia" icon="🧯" action={()=>nav('/magazzino')} actionLabel="Magazzino →">
          <MaterialiEsauriti />
        </SectionCard>
      </div>

      {/* MAPPA FLOTTA */}
      <SectionCard title="Posizione flotta" icon="🚐" action={()=>nav('/flotta')} actionLabel="Apri mappa completa →">
        <MiniMappa />
      </SectionCard>
    </div>
  );
}
